import { useEffect, useCallback } from "react";
import { navigate } from "@reach/router";
import { Button } from "newui/components/Button";
import SkeletonBase from "newui/components/Skeleton";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";
import { useHasAIEvaluationAccess } from "hooks/featureFlags";
import { useRfxData } from "newui/features/Rfx/data-provider/RfxDataProvider";
import { RFX_TABS } from "utils/constants";
import { useEvaluation } from "../hooks/useEvaluation";
import { InitialEvaluationProvider } from "../hooks/InitialEvaluationContext";
import { RFxEvaluationSteps } from "../components/RFxEvaluationSteps";
import { EvaluationAccessBoundary } from "../components/EvaluationAccessBoundary";
import { RfxLeadInfoBox } from "../components/steps/InitialEvaluationStep/panel";
import { StepperLegend } from "newui/components/Stepper";
import type { PanelMode } from "../types/panel.type";
import { isSoloPanelMode } from "../utils/panelModes";

interface EvaluationPageProps {
	evaluationId?: string;
	path?: string;
}

export const EvaluationPage: React.FC<EvaluationPageProps> = () => {
	const { rfxData } = useRfxData();
	const rfxId = rfxData?.id;
	const { clientPaths } = useAuthContext();
	const hasAIAccess = useHasAIEvaluationAccess();

	const { isLoading, evaluationState, currentUserContext, accessError, fetchEvaluationState, updateProgress, advanceStep } = useEvaluation({
		rfxId,
	});

	useEffect(() => {
		fetchEvaluationState();
	}, [fetchEvaluationState]);

	const handleBack = (): void => {
		navigate(clientPaths.viewRfx(rfxData, RFX_TABS.EVALUATION));
	};

	// Step changes are UI-only navigation — the backend owns step progression
	// via the advance-step endpoint, called by each step's onBeforeNext.
	const handleStepChange = useCallback(
		(_step: number) => {
			// No-op: backend step is advanced via advanceStep(), not here.
		},
		[]
	);

	const handlePanelModeChange = useCallback(
		(mode: PanelMode) => {
			updateProgress({ panel_mode: mode });
		},
		[updateProgress]
	);

	if (isLoading) {
		return (
			<div className="space-y-3 p-6">
				<SkeletonBase
					startColor="lightgray"
					width="100%"
					height="48px"
					className="!rounded-lg"
				/>
				<SkeletonBase
					startColor="lightgray"
					width="60%"
					height="40px"
					className="!rounded-lg"
				/>
			</div>
		);
	}

	return (
		<EvaluationAccessBoundary
			access={currentUserContext?.access}
			error={accessError}
			rfxData={rfxData}
			evaluationState={evaluationState}
			currentUserContext={currentUserContext}
			onAccept={fetchEvaluationState}
			onDecline={fetchEvaluationState}
		>
			<InitialEvaluationProvider rfxId={rfxId}>
				<div data-testid="evaluation-page" className="p-6">
					<div className="mb-4 flex flex-row items-center">
						<Button btntype="secondary" onClick={handleBack}>
							← Back to RFx
						</Button>
						<div className="ml-4 flex flex-col">
							<div>
								RFx: {rfxData?.name}
							</div>
							{rfxData?.project && <div>
								Project: {rfxData?.project?.name}
							</div>}
						</div>
						<StepperLegend className="ml-auto flex flex-wrap items-center justify-end gap-4 border border-gray-200 p-4 rounded" />
					</div>
					<div className="mb-6 flex items-start justify-between">
						<div>
							<h1 className="text-2xl font-semibold text-gray-900">
								{hasAIAccess ? (
									<>EvaluA<span className="text-utility-brand-700">I</span>tion tool</>
								) : (
									"Evaluation"
								)}
							</h1>
							<p className="mt-1 text-sm text-gray-500">
								{hasAIAccess
									? "Use AI-assisted evaluation to systematically score and compare supplier submissions."
									: "Systematically score and compare supplier submissions."}
							</p>
						</div>
						<div className="flex items-center gap-3 shrink-0">
							{!currentUserContext?.isRfxLead && rfxData?.rfxLead && (
								<RfxLeadInfoBox rfxLead={rfxData.rfxLead} />
							)}
							{currentUserContext?.isRfxLead && (
								<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-lg font-medium bg-purple-100 text-purple-700">
									RFx Lead
								</span>
							)}
							{!isSoloPanelMode(evaluationState?.panelMode) && (
								currentUserContext?.panelMemberId != null ? (
									<span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-lg font-medium text-blue-700">
										Panel Member
									</span>
								) : (
									<span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-lg font-medium text-amber-700">
										Non-Panel Member
									</span>
								)
							)}
						</div>
					</div>
					<RFxEvaluationSteps
						rfxData={rfxData}
						evaluationState={evaluationState}
						isRfxLead={currentUserContext?.isRfxLead ?? false}
						onComplete={handleBack}
						onStepChange={handleStepChange}
						onPanelModeChange={handlePanelModeChange}
						onAdvanceStep={advanceStep}
						onRefreshUserContext={fetchEvaluationState}
					/>
				</div>
			</InitialEvaluationProvider>
		</EvaluationAccessBoundary>
	);
};
